import React, { useContext, useEffect, useState, Component } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiAlertCircle, FiCreditCard } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { useApi } from '../hooks/useApi';
import { readApiJson } from '../utils/apiResponse';
import { apiUrl } from '../config';
import { resolvePostPaymentDestination } from '../utils/onboarding';
import { normalizeMercadoPagoReturnParams } from '../utils/mpReturnParams';
import TransactionalEmailHint from '../components/auth/TransactionalEmailHint';

const REDIRECT_DELAY_MS = 1200;

function scheduleAccountEntry(navigate, user, options = {}) {
  const path = resolvePostPaymentDestination(user, options);
  window.setTimeout(() => navigate(path, { replace: true }), REDIRECT_DELAY_MS);
}

const PagoResultado = () => {
  const { refreshUser, login, token: authToken, user: authUser } = useContext(AuthContext);
  const { secureFetch } = useApi();
  const tc = useThemeClasses();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'subscription';
  const isSignup = source === 'signup';
  const isOverage = source === 'overage';
  const isOcrOverage = source === 'ocr_overage';
  const isInvoice = source === 'invoice';
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState(
    isOcrOverage
      ? 'Estamos confirmando tu compra de escaneos OCR...'
      : isOverage
        ? 'Estamos confirmando tu compra de registros extra...'
        : isInvoice
          ? 'Estamos confirmando el pago del comprobante...'
          : isSignup
            ? 'Recibimos tu pago. Estamos activando tu cuenta en Contsuite…'
            : 'Estamos confirmando tu suscripción con Mercado Pago...'
  );
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const syncOcrOveragePayment = async () => {
      const paymentId = searchParams.get('payment_id')
        || searchParams.get('collection_id')
        || searchParams.get('paymentId');
      const externalReference = searchParams.get('external_reference')
        || searchParams.get('externalReference');
      const params = new URLSearchParams();
      if (paymentId) params.set('paymentId', paymentId);
      if (externalReference) params.set('externalReference', externalReference);

      const response = await secureFetch(`/api/payments/ocr/status?${params.toString()}`);
      const data = await readApiJson(response);

      if (cancelled) return;

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'No pudimos confirmar el pago.');
        return;
      }

      const finalStatus = String(data.status || searchParams.get('status') || '').toLowerCase();
      if (finalStatus === 'approved') {
        setStatus('success');
        setMessage(
          data.scansAdded
            ? `Se acreditaron +${data.scansAdded} escaneos OCR para este mes.`
            : 'Tu compra de escaneos OCR quedó acreditada.'
        );
        setTimeout(() => navigate('/uso-plan#ocr-packs'), 2500);
        return;
      }

      if (['pending', 'in_process'].includes(finalStatus)) {
        setStatus('pending');
        setMessage('Tu pago está pendiente. Revisá el estado en unos minutos desde Uso del plan.');
        return;
      }

      setStatus('error');
      setMessage('No pudimos confirmar la compra de escaneos. Podés reintentar desde Uso del plan.');
    };

    const syncOveragePayment = async () => {
      const paymentId = searchParams.get('payment_id')
        || searchParams.get('collection_id')
        || searchParams.get('paymentId');
      const externalReference = searchParams.get('external_reference')
        || searchParams.get('externalReference');
      const params = new URLSearchParams();
      if (paymentId) params.set('paymentId', paymentId);
      if (externalReference) params.set('externalReference', externalReference);

      const response = await secureFetch(`/api/payments/overage/status?${params.toString()}`);
      const data = await readApiJson(response);

      if (cancelled) return;

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'No pudimos confirmar el pago.');
        return;
      }

      const finalStatus = String(data.status || searchParams.get('status') || '').toLowerCase();
      if (finalStatus === 'approved') {
        setStatus('success');
        setMessage(
          data.documentsAdded
            ? `Se acreditaron +${data.documentsAdded} registros extra para este mes.`
            : 'Tu compra de registros extra quedó acreditada.'
        );
        setTimeout(() => navigate('/uso-plan'), 2500);
        return;
      }

      if (['pending', 'in_process'].includes(finalStatus)) {
        setStatus('pending');
        setMessage('Tu pago está pendiente. Revisá el estado en unos minutos desde Uso del plan.');
        return;
      }

      setStatus('error');
      setMessage('No pudimos confirmar la compra. Podés reintentar desde Uso del plan.');
    };

    const syncInvoicePayment = async () => {
      const paymentId = searchParams.get('payment_id')
        || searchParams.get('collection_id')
        || searchParams.get('paymentId');
      const params = new URLSearchParams();
      if (paymentId) params.set('paymentId', paymentId);

      const response = await secureFetch(`/api/payments/invoice/status?${params.toString()}`);
      const data = await readApiJson(response);

      if (cancelled) return;

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'No pudimos confirmar el pago.');
        return;
      }

      const finalStatus = String(data.status || searchParams.get('status') || '').toLowerCase();
      if (finalStatus === 'approved') {
        setStatus('success');
        setMessage(
          data.numero
            ? `Pago acreditado en comprobante N°${data.numero}.`
            : 'El pago del comprobante quedó acreditado.'
        );
        setTimeout(() => navigate('/cobranzas'), 2500);
        return;
      }

      if (['pending', 'in_process'].includes(finalStatus)) {
        setStatus('pending');
        setMessage('Tu pago está pendiente. Revisá el estado en unos minutos desde Cobrar.');
        return;
      }

      setStatus('error');
      setMessage('No pudimos confirmar el pago del comprobante.');
    };

    const fetchSignupSession = async (lookup = {}) => {
      const params = new URLSearchParams();
      if (lookup.preapprovalId) params.set('preapprovalId', lookup.preapprovalId);
      if (lookup.externalReference) params.set('externalReference', lookup.externalReference);
      if (lookup.email) params.set('email', lookup.email);
      if ([...params.keys()].length === 0) return null;

      const response = await fetch(apiUrl(`/api/signup/session?${params.toString()}`));
      const data = await readApiJson(response);
      if (!response.ok || !data.token || !data.user) return null;
      login(data.user, data.token, data.refreshToken);
      return data;
    };

    const fetchSignupSessionWithRetry = async (lookup, attempts = 8) => {
      for (let i = 0; i < attempts; i += 1) {
        const data = await fetchSignupSession(lookup);
        if (data) return data;
        if (i < attempts - 1) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      return null;
    };

    const resolveSignupLookup = () => {
      const normalized = normalizeMercadoPagoReturnParams(searchParams);
      let preapprovalId = normalized.preapprovalId;
      let externalReference = normalized.externalReference;
      let email = '';
      try {
        if (!preapprovalId) {
          preapprovalId = sessionStorage.getItem('contsuite_signup_preapproval') || '';
        }
        if (!externalReference) {
          externalReference = sessionStorage.getItem('contsuite_signup_external_reference') || '';
        }
        email = sessionStorage.getItem('contsuite_signup_email') || '';
      } catch {
        /* ignore */
      }
      return {
        preapprovalId: preapprovalId || undefined,
        externalReference: externalReference || undefined,
        email: email || undefined,
      };
    };

    const syncSubscriptionPayment = async () => {
      const signupLookup = resolveSignupLookup();
      const preapprovalId = signupLookup.preapprovalId;

      const hasSignupLookup = Boolean(
        signupLookup.preapprovalId || signupLookup.externalReference || signupLookup.email
      );

      const isSignupReturn = isSignup || hasSignupLookup;

      if (isSignupReturn && hasSignupLookup) {
        const signupData = await fetchSignupSessionWithRetry(signupLookup);
        if (cancelled) return;
        if (signupData) {
          setStatus('success');
          setMessage('Pago confirmado. Te llevamos a tu cuenta…');
          try {
            sessionStorage.removeItem('contsuite_signup_preapproval');
            sessionStorage.removeItem('contsuite_signup_external_reference');
            sessionStorage.removeItem('contsuite_signup_email');
            sessionStorage.removeItem('contsuite_signup_awaiting');
            sessionStorage.removeItem('contsuite_signup_continue_url');
          } catch {
            /* ignore */
          }
          scheduleAccountEntry(navigate, signupData.user, { newSignup: true });
          return;
        }
        if (isSignupReturn) {
          setStatus('pending');
        setMessage(
          isSignup
            ? 'Tu pago en Mercado Pago está confirmado y tu cuenta se está activando. '
              + 'Si no entrás automáticamente en unos segundos, usá «Activar cuenta» abajo o el enlace del email que te enviamos.'
            : 'El pago en Mercado Pago fue registrado. Si la pantalla de MP no te redirigió, tocá «Volver al sitio» o «Ir al comercio». '
              + 'Si sigue pendiente, el webhook puede tardar 1–2 minutos.'
        );
          return;
        }
      }

      const query = preapprovalId ? `?preapprovalId=${encodeURIComponent(preapprovalId)}` : '';
      const response = await secureFetch(`/api/payments/subscription/status${query}`);
      const data = await readApiJson(response);

      if (cancelled) return;

      if (!response.ok) {
        if (!authToken && hasSignupLookup) {
          const signupData = await fetchSignupSession(signupLookup);
          if (signupData) {
            setStatus('success');
            setMessage('Pago confirmado. Te llevamos a tu cuenta…');
            scheduleAccountEntry(navigate, signupData.user, { newSignup: true });
            return;
          }
        }
        setStatus('error');
        setMessage(data.message || 'No pudimos confirmar el pago.');
        return;
      }

      await refreshUser();

      const finalStatus = String(data.status || '').toLowerCase();
      if (['active', 'trialing', 'authorized'].includes(finalStatus)) {
        let sessionUser = null;
        if (!authToken && preapprovalId) {
          const signupData = await fetchSignupSession({ preapprovalId, ...signupLookup });
          sessionUser = signupData?.user || null;
        }
        await refreshUser();
        setStatus('success');
        const recoverySuffix = data.recoveryMessage ? ` ${data.recoveryMessage}` : ' Tu información se mantuvo intacta.';
        setMessage(
          sessionUser
            ? 'Pago confirmado. Te llevamos a tu cuenta…'
            : finalStatus === 'trialing'
              ? `Tu prueba gratis quedó activa. Mercado Pago cobrará automáticamente cuando termine el período de prueba.${recoverySuffix}`
              : `Tu suscripción quedó activa correctamente.${recoverySuffix}`
        );
        scheduleAccountEntry(navigate, sessionUser || authUser, { newSignup: Boolean(sessionUser) });
        return;
      }

      if (finalStatus === 'pending_payment') {
        setStatus('pending');
        setMessage('Tu pago está pendiente de confirmación. Podés volver a intentarlo desde Planes si no se acredita en unos minutos.');
        return;
      }

      setStatus('pending');
      setMessage('Estamos procesando tu suscripción. Si no se actualiza en unos minutos, revisá el estado desde Planes.');
    };

    const syncPayment = async () => {
      try {
        if (isOcrOverage) {
          await syncOcrOveragePayment();
        } else if (isOverage) {
          await syncOveragePayment();
        } else if (isInvoice) {
          await syncInvoicePayment();
        } else {
          await syncSubscriptionPayment();
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setMessage(err.message || 'Error de conexión al confirmar el pago.');
        }
      }
    };

    syncPayment();
    return () => {
      cancelled = true;
    };
  }, [authToken, authUser, isInvoice, isOcrOverage, isOverage, isSignup, login, navigate, refreshUser, retryTick, searchParams, secureFetch]);

  const icon = {
    loading: <FiClock className="h-10 w-10 animate-pulse" />,
    success: <FiCheckCircle className="h-10 w-10 text-emerald-500" />,
    pending: <FiCreditCard className="h-10 w-10 text-amber-500" />,
    error: <FiAlertCircle className="h-10 w-10 text-red-500" />,
  }[status];

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${tc.modoOscuro ? 'bg-black' : 'bg-gray-50'}`}>
      <div className={`w-full max-w-lg rounded-2xl border p-8 text-center ${tc.box}`}>
        <div className="flex justify-center mb-4">{icon}</div>
        <h1 className={`text-2xl font-semibold mb-3 ${tc.textPri}`}>
          {status === 'loading' && 'Confirmando pago'}
          {status === 'success' && (isOcrOverage || isOverage ? '¡Compra confirmada!' : isInvoice ? '¡Pago confirmado!' : '¡Suscripción confirmada!')}
          {status === 'pending' && 'Pago pendiente'}
          {status === 'error' && 'No pudimos confirmar el pago'}
        </h1>
        <p className={`${tc.textMuted} mb-6`}>{message}</p>
        {isSignup && (status === 'pending' || status === 'success') && (
          <TransactionalEmailHint className="mb-4 text-left" />
        )}
        {isSignup && (status === 'pending' || status === 'loading') && (
          <p className={`text-sm mb-4 ${tc.textSec}`}>
            No cerramos tu pago: Mercado Pago ya lo acreditó. Contsuite solo termina de vincular tu suscripción a tu usuario.
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isSignup && (status === 'pending' || status === 'error') ? (
            <>
              <Link to="/login" className={`px-4 py-2 rounded-lg ${tc.btnPrimary}`}>
                Entrar a mi cuenta
              </Link>
              <button
                type="button"
                onClick={() => setRetryTick((n) => n + 1)}
                className={`px-4 py-2 rounded-lg ${tc.btnSecondary}`}
              >
                Reintentar activación
              </button>
            </>
          ) : (
            <>
              <Link to="/inicio" className={`px-4 py-2 rounded-lg ${tc.btnPrimary}`}>
                Ir al inicio
              </Link>
              {isOcrOverage || isOverage ? (
                <Link to={isOcrOverage ? '/uso-plan#ocr-packs' : '/uso-plan'} className={`px-4 py-2 rounded-lg ${tc.btnSecondary}`}>
                  Uso del plan
                </Link>
              ) : isInvoice ? (
                <Link to="/cobranzas" className={`px-4 py-2 rounded-lg ${tc.btnSecondary}`}>
                  Ir a Cobrar
                </Link>
              ) : !isSignup ? (
                <Link to="/planes" className={`px-4 py-2 rounded-lg ${tc.btnSecondary}`}>
                  Ver planes
                </Link>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

class PagoResultadoErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50 text-gray-900">
          <div className="max-w-lg text-center space-y-4 p-8 border rounded-2xl bg-white shadow-sm">
            <h1 className="text-2xl font-semibold">Pago recibido</h1>
            <p className="text-gray-600">
              Mercado Pago confirmó tu pago. Si esta pantalla quedó en blanco, tu cuenta igual puede estar activa.
            </p>
            <p className="text-gray-600 text-sm">
              Entrá con el mismo email del registro. Revisá también la bandeja de entrada (email «Pago confirmado»).
            </p>
            <Link
              to="/login"
              className="inline-block px-4 py-2 rounded-lg bg-[#1F80FF] text-white font-semibold"
            >
              Entrar a Contsuite
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PagoResultadoPage() {
  return (
    <PagoResultadoErrorBoundary>
      <PagoResultado />
    </PagoResultadoErrorBoundary>
  );
}

export default PagoResultadoPage;
